import { useEffect, useState } from "react";
import { Grid, InputAdornment, IconButton, Paper, TextField, TablePagination, Button } from "@mui/material";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import styles from "./styles.module.css";
import { ROUTES } from "const";
import * as PurchaseHelper from "helpers/PurchaseHelper";
import SkeletonLoading from "components/utils/Loading/SkeletonLoading";
import DataTable from "components/utils/DataTable";
import Info from "components/utils/Alert/Info";
import SearchIcon from "images/svg/search.svg";
import PurchaseDetailsDialog from "components/popups/PurchaseDetailsDialog/PurchaseDetailsDialog";
import { ReactComponent as CartIcon } from "images/svg/shopping-cart.svg";
import { ReactComponent as DraftIcon } from "images/svg/draft.svg";

function PurchasePage() {
  const [purchases, setPurchases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterText, setFilterText] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [selectedPurchase, setSelectedPurchase] = useState(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);

  const navigate = useNavigate();

  const handleNewPurchase = () => {
    navigate(ROUTES.NEW_PURCHASE);
  };

  const handleRowClick = (purchase) => {
    setSelectedPurchase(purchase);
    setIsDetailsOpen(true);
  };

  const handleCloseDetails = () => {
    setIsDetailsOpen(false);
    setSelectedPurchase(null);
  };

  const headerData = [
    {
      label: "Invoice No",
      id: "invoiceNo",
      type: "text",
    },
    {
      label: "Date",
      id: "billDate",
      type: "text",
    },
    {
      label: "Vendor",
      id: "vendor.name",
      type: "text",
    },
    {
      label: "Total Amount",
      id: "totalPurchaseAmount",
      type: "floatAmount",
      align: "right",
    },
    {
      label: "Paid Amount",
      id: "paidAmount",
      type: "floatAmount",
      align: "right",
    },
  ];

  const fetchPurchases = async () => {
    setLoading(true);
    try {
      const data = await PurchaseHelper.fetchTodayPurchases();
      setPurchases(data || []);
    } catch (error) {
      toast.error(error.message || "Failed to fetch purchases");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchPurchases();
  }, []);

  const handleSearch = (value) => {
    setFilterText(value);
  };

  const filteredPurchases = purchases.filter(
    (p) =>
      (p.invoice_no || "").toLowerCase().includes(filterText.toLowerCase()) ||
      (p.vendor?.name || "").toLowerCase().includes(filterText.toLowerCase()),
  );

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedPurchases = filteredPurchases.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Grid style={{ padding: "10px", height: "100%" }}>
      <Grid container className={styles.container} rowGap={1.25}>
        <Grid item xs={12} className={styles.headerContainer}>
          <Grid className={styles.header}>
            <h2>Purchase Management</h2>
            <Grid>
              <h5>View and track today's purchases</h5>
            </Grid>
          </Grid>

          <Grid item xs={12} className={styles.contentWrapper}>
            <TextField
              placeholder="Search purchase"
              size="small"
              className={styles.searchField}
              value={filterText}
              onChange={(e) => handleSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="end">
                    <IconButton edge="end" style={{ padding: "0" }}>
                      <img src={SearchIcon} alt="Search" className={styles.icon} />
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <div className={styles.bar}> </div>
            <Button onClick={() => navigate(ROUTES.DRAFT_PURCHASES)} className={styles.addBtn}>
              <DraftIcon className={styles.icon} />
              Drafts
            </Button>
            <Button onClick={handleNewPurchase} className={styles.addBtn}>
              <CartIcon className={styles.icon} />
              Add New Purchase
            </Button>
          </Grid>
        </Grid>
        <Grid item xs={12} className={styles.tableContainer}>
          {loading ? (
            <SkeletonLoading isOpen={true} count={40} height={40} xs={12} lg={1.2} />
          ) : filteredPurchases && filteredPurchases.length ? (
            <Paper
              elevation={0}
              style={{
                display: "flex",
                flexDirection: "column",
                height: "100%",
              }}
            >
              <div style={{ flex: 1, overflowY: "auto" }}>
                <DataTable columns={headerData} rows={paginatedPurchases} rowClickListener={handleRowClick} />
              </div>
              <div className={styles.pagination}>
                <TablePagination
                  rowsPerPageOptions={[10, 20, 50]}
                  component="div"
                  count={filteredPurchases.length}
                  rowsPerPage={rowsPerPage}
                  page={page}
                  onPageChange={handleChangePage}
                  onRowsPerPageChange={handleChangeRowsPerPage}
                />
              </div>
            </Paper>
          ) : (
            <Info title="You have no purchases to list" content="You haven't any purchase to list for today." />
          )}
        </Grid>
      </Grid>
      <PurchaseDetailsDialog
        open={isDetailsOpen}
        onClose={handleCloseDetails}
        purchase={selectedPurchase}
        onRefresh={fetchPurchases}
      />
    </Grid>
  );
}

export default PurchasePage;
